import { getUser } from '@frontend/atoms/opalUserAtom';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import type { StreamEvent } from '../features/agents/types/streaming';

export type SSEClientOptions = {
  url: string;
  body: Record<string, unknown>;
  headers?: Record<string, string>;
  onEvent: (event: StreamEvent) => void;
  onError: (error: { message: string; status?: number }) => void;
  signal?: AbortSignal;
};

/**
 * Parse SSE data-only lines from a text chunk.
 * Backend emits `data: {json}\n\n` — no `event:` field.
 * Returns parsed events and any remaining incomplete line (buffer).
 */
export function parseSSEChunk(
  text: string,
  buffer: string
): { events: StreamEvent[]; remaining: string } {
  const combined = buffer + text;
  const events: StreamEvent[] = [];

  // Split on double newline (SSE event boundary)
  const parts = combined.split('\n\n');

  // Last part may be incomplete — keep as buffer
  const remaining = parts.pop() ?? '';

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Extract data from `data: {json}` line(s)
    const lines = trimmed.split('\n');
    let dataStr = '';
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        dataStr += line.slice(6);
      } else if (line.startsWith('data:')) {
        dataStr += line.slice(5);
      }
    }

    if (!dataStr) continue;

    try {
      const parsed = JSON.parse(dataStr) as StreamEvent;
      events.push(parsed);
    } catch {
      // Skip malformed JSON lines
    }
  }

  return { events, remaining };
}

/**
 * Build auth headers for SSE fetch requests.
 * Uses the same auth pattern as the HttpClient (Bearer token + X-Instance-Id).
 */
async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const token = await getOrRenewOptiIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (err) {
    // Token renewal failed — proceed without an Authorization header. The
    // backend will reject with 401 and the caller's onError handles it, but
    // surface the underlying renewal failure to Sentry so we can correlate
    // it with backend "did not find opti-id token" logs instead of debugging
    // the symptom in isolation.
    sendSentryError(err instanceof Error ? err : new Error('SSE token renewal failed'), {
      context: 'sse.buildAuthHeaders',
      operation: 'token_attach',
    });
  }

  try {
    const user = getUser();
    if (user?.instance?.instance_id) {
      headers['X-Instance-Id'] = user.instance.instance_id;
    }
    // X-Opal-Region must mirror the HTTP client (see http.ts) so SSE streams
    // land on the same regional backend the rest of this tab is using.
    if (user?.instance) {
      headers['X-Opal-Region'] = user.instance.hosted_region || 'US';
    }
  } catch {
    // User not available
  }

  return headers;
}

/**
 * Fetch-based SSE client for streaming POST requests.
 * Uses response.body.getReader() to process data-only SSE lines.
 */
export async function createSSEStream(options: SSEClientOptions): Promise<void> {
  const { url, body, headers: extraHeaders, onEvent, onError, signal } = options;

  const authHeaders = await buildAuthHeaders();
  const headers = { ...authHeaders, ...extraHeaders };

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) return;
    onError({
      message: err instanceof Error ? err.message : 'Network error',
    });
    return;
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const errorBody = await response.text();
      const parsed = JSON.parse(errorBody);
      const rawDetail = parsed.detail;
      if (Array.isArray(rawDetail) && rawDetail.length > 0) {
        // Pydantic validation error: [{type, loc, msg, input, ctx}]
        const firstError = rawDetail[0];
        detail =
          firstError?.msg && typeof firstError.msg === 'string'
            ? firstError.msg
            : JSON.stringify(rawDetail);
      } else if (typeof rawDetail === 'string') {
        detail = rawDetail;
      } else if (rawDetail?.message && typeof rawDetail.message === 'string') {
        detail = rawDetail.message;
      }
    } catch {
      // Use status code as message
    }
    onError({ message: detail, status: response.status });
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError({ message: 'Response body is not readable' });
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      const { events, remaining } = parseSSEChunk(text, buffer);
      buffer = remaining;

      for (const event of events) {
        onEvent(event);
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const { events } = parseSSEChunk(buffer + '\n\n', '');
      for (const event of events) {
        onEvent(event);
      }
    }
  } catch (err) {
    if (signal?.aborted) return;
    onError({
      message: err instanceof Error ? err.message : 'Stream read error',
    });
  }
}
